import { AnyAction } from '@reduxjs/toolkit';
import { combineEpics, StateObservable } from 'redux-observable';
import { concat, filter, map, Observable, of, switchMap } from 'rxjs';
import { SmoLanguage } from '../../../../Models/Smo/SmoLanguage';
import { UserSettings } from '../../../../Models/User/UserSettings';
import { mapPayload } from '../../../../Utils/RxjsOperators/MapPayload';
import { TextReader } from '../../../TextReader/TextReader';
import { TextReaderLanguageConverter } from '../../../TextReaderLanguageConverter/TextReaderLanguageConverter';
import { UserManager } from '../../../UserManager/UserManager';
import { StoreDependencies } from '../../Global/StoreDependencies';
import { StoreState } from '../../Global/StoreState';
import TextReaderActions from '../../Global/TextReader/TextReaderActions';
import UserStoreActions from '../../Global/User/UserStoreActions';
import VideoAnalysisActions from '../../Global/VideoAnalysis/VideoAnalysisActions';
import SmoLanguageMenuActions from './SmoLanguageMenuActions';

function updateLanguage(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(SmoLanguageMenuActions.updateLanguage.match),
    mapPayload(),
    map((language) => ({
      language,
      settings: $state.value.user.currentUser?.settings
    })),
    filter(({ settings }) => !!settings),
    filter(({ language, settings }) => language !== settings!.smoLanguage),
    switchMap(({ language, settings }) =>
      concat(
        updateTextReaderLanguage(language, dependencies.textReader!, dependencies.textReaderLanguageConverter!),
        updateCurrentUserSmoLanguage(language, settings!, dependencies.userManager!),
        of(VideoAnalysisActions.resetFilterBrightness())
      )
    )
  );
}

function updateTextReaderLanguage(
  smoLanguage: SmoLanguage,
  textReader: TextReader,
  textReaderLanguageConverter: TextReaderLanguageConverter<SmoLanguage>
): Observable<AnyAction> {
  return of(textReaderLanguageConverter.getTextReaderLanguageForGameLanguage(smoLanguage)).pipe(
    switchMap((language) => textReader.setLanguage(language)),
    map(TextReaderActions.setLanguage)
  );
}

function updateCurrentUserSmoLanguage(
  smoLanguage: SmoLanguage,
  settings: UserSettings,
  userManager: UserManager
): Observable<AnyAction> {
  return userManager.updateSettings({ ...settings, smoLanguage }).pipe(map(UserStoreActions.setCurrentUserSettings));
}

export default combineEpics(updateLanguage);
